import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { usePageData } from "@/hooks/usePageData";
import { useSEO } from "@/hooks/useSEO";
import {
  FileText, Shield, Scale, Briefcase, ArrowRight,
  Building2, Globe, Users, TrendingUp, ChevronRight,
  Handshake, Eye, Network, Landmark, Lightbulb, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconMap = { Building2, FileText, Shield, Scale, Briefcase, TrendingUp, Globe, Users, Landmark, Handshake, Network, Eye, Lightbulb, Clock };
const getIcon = (name) => iconMap[name] || Briefcase;

export default function HomePage() {
  const { sections } = usePageData("home");
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    axios.get(`${API}/insights`).then(res => setInsights(res.data.slice(0, 3))).catch(() => {});
  }, []);

  const hero = sections.hero || {};
  const whoWeServe = sections.who_we_serve || {};
  const pillars = sections.pillars || {};
  const differentiators = sections.differentiators || {};
  const cta = sections.cta || {};
  const pageTitle = hero.meta_title || "Equaliser Legal LLP | Corporate Legal Advisory";
  const pageDesc = hero.meta_description || "Corporate legal advisory and outsourced legal desk for businesses in India and abroad, with a focus on contracts, compliance, and international commercial disputes.";
  useSEO(pageTitle, pageDesc);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative bg-[#0A1628] noise-overlay min-h-[85vh] flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 opacity-0 animate-fade-in-up">
              <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-6 font-body">{hero.subtitle || "Equaliser Legal LLP"}</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-tight tracking-tight mb-6">{hero.title || "Corporate legal advisory for businesses that want clarity and control."}</h1>
              <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-2xl mb-10 font-body font-light">{hero.body || ""}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={hero.cta_link || "/contact"} data-testid="hero-talk-to-us" className="inline-flex items-center justify-center bg-[#C5A059] text-[#0A1628] px-8 py-3.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#d4af6a] transition-colors duration-300 rounded-[2px]">{hero.cta_text || "Talk to us"}</Link>
                <Link to={hero.cta2_link || "/services"} data-testid="hero-explore-services" className="inline-flex items-center justify-center border border-white/30 text-white px-8 py-3.5 text-sm uppercase tracking-[0.15em] font-medium hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300 rounded-[2px]">{hero.cta2_text || "Explore our services"} <ArrowRight size={16} className="ml-2" /></Link>
              </div>
            </div>
            <div className="hidden lg:block lg:col-span-2">
              <div className="relative opacity-0 animate-fade-in-up animate-delay-300">
                <div className="aspect-[4/5] overflow-hidden rounded-[2px]">
                  <img src={hero.image_url || "https://images.pexels.com/photos/35268841/pexels-photo-35268841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt="Corporate advisory" className="w-full h-full object-cover opacity-40" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#0A1628] to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section data-testid="who-we-serve" className="bg-[#F8FAFC] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{whoWeServe.subtitle || "Who We Serve"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{whoWeServe.title || "Clients who value clarity and commercial sense."}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(whoWeServe.items || [
              { title: "Growth-Stage & Mid-Market Companies", description: "Practical legal support scaled to your business needs in India.", icon: "TrendingUp" },
              { title: "Indian Corporates Going Global", description: "Doing business in the Middle East and UK with confidence.", icon: "Globe" },
              { title: "International Clients", description: "Navigating India's legal landscape with local expertise.", icon: "Landmark" },
              { title: "Investors & Family Offices", description: "Due diligence, governance, and portfolio legal support.", icon: "Users" }
            ]).map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={i} data-testid={`audience-tile-${i}`} className="bg-white border border-slate-200 p-8 hover:border-[#0A1628] transition-colors duration-300 group">
                  <Icon size={24} className="text-slate-400 group-hover:text-[#C5A059] transition-colors duration-300 mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mb-3">{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Pillars */}
      <section data-testid="key-pillars" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{pillars.subtitle || "What We Do"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{pillars.title || "Four pillars of our practice."}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(pillars.items || [
              { title: "Corporate & Commercial", points: ["Company policies and board processes","Shareholder arrangements","Corporate restructuring advisory"], icon: "Building2" },
              { title: "Contracts & Negotiations", points: ["Vendor and customer contracts","MSAs, SOWs, NDAs","Employment and consulting agreements"], icon: "FileText" },
              { title: "Compliance & Governance", points: ["Compliance frameworks and checklists","Policy drafting (POSH, data protection)","Training and internal awareness"], icon: "Shield" },
              { title: "Disputes Strategy & Oversight", points: ["Commercial disputes assessment","Arbitration and litigation coordination","India, Middle East and UK coverage"], icon: "Scale" }
            ]).map((p, i) => {
              const Icon = getIcon(p.icon);
              return (
                <div key={i} data-testid={`pillar-card-${i}`} className="bg-white border border-slate-200 p-8 md:p-10 hover:border-[#0A1628] transition-colors duration-300 group">
                  <div className="flex items-start justify-between mb-6">
                    <Icon size={28} className="text-slate-400 group-hover:text-[#C5A059] transition-colors duration-300" strokeWidth={1.5} />
                    <Link to="/services" className="text-slate-300 group-hover:text-[#C5A059] transition-colors"><ChevronRight size={20} /></Link>
                  </div>
                  <h3 className="font-heading text-xl text-[#0A1628] font-medium mb-4">{p.title}</h3>
                  <ul className="space-y-2">
                    {(p.points || []).map((pt, j) => <li key={j} className="text-slate-500 text-sm leading-relaxed flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-[#C5A059] mt-2 flex-shrink-0"></span>{pt}</li>)}
                  </ul>
                  <Link to="/services" className="inline-flex items-center text-sm text-[#0A1628] hover:text-[#C5A059] transition-colors duration-300 mt-6 uppercase tracking-wider font-medium">Learn more <ArrowRight size={14} className="ml-1" /></Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Equaliser */}
      <section data-testid="why-equaliser" className="bg-[#F8FAFC] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">{differentiators.subtitle || "Why Equaliser Legal"}</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight mb-16">{differentiators.title || "What sets us apart."}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(differentiators.items || [
              { title: "Business-First Approach", description: "Advice that is legally sound and commercially practical.", icon: "Briefcase" },
              { title: "Outsourced Legal Desk", description: "Fractional General Counsel model for corporates and startups.", icon: "Handshake" },
              { title: "9-Lawyer Partner Firm", description: "Seamless coordination with our associated law firm in India.", icon: "Network" },
              { title: "Cross-Border Reach", description: "Advisory coverage across India, the Middle East, and the UK.", icon: "Eye" }
            ]).map((item, i) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={i} data-testid={`differentiator-${i}`} className="group">
                  <Icon size={24} className="text-[#C5A059] mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mb-2">{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insights Preview */}
      {insights.length > 0 && (
        <section data-testid="insights-preview" className="bg-white py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-[#C5A059] text-xs uppercase tracking-[0.25em] mb-4">Insights</p>
                <h2 className="font-heading text-3xl md:text-4xl text-[#0A1628] font-medium tracking-tight">Recent publications.</h2>
              </div>
              <Link to="/insights" data-testid="view-all-insights" className="hidden md:inline-flex items-center text-sm text-[#0A1628] hover:text-[#C5A059] transition-colors uppercase tracking-wider font-medium">View all <ArrowRight size={14} className="ml-1" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {insights.map((insight, i) => (
                <Link to={`/insights/${insight.id}`} key={insight.id} data-testid={`insight-preview-${i}`} className="border border-slate-200 p-8 hover:border-[#0A1628] transition-colors duration-300 group">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs uppercase tracking-wider text-[#C5A059]">{insight.category}</span>
                    <span className="text-slate-300">|</span>
                    <span className="text-xs text-slate-400">{insight.published_date}</span>
                  </div>
                  <h3 className="font-heading text-lg text-[#0A1628] font-medium mb-3 group-hover:text-[#C5A059] transition-colors duration-300 leading-snug">{insight.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{insight.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="cta-section" className="bg-[#0A1628] noise-overlay relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white font-medium tracking-tight mb-6">{cta.title || "Need to get your contracts and compliance in order?"}</h2>
          <p className="text-white/50 text-base md:text-lg mb-10 max-w-2xl mx-auto">{cta.body || "We help businesses build legal clarity from the ground up."}</p>
          <Link to={cta.cta_link || "/contact"} data-testid="cta-schedule-conversation" className="inline-flex items-center justify-center bg-[#C5A059] text-[#0A1628] px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#d4af6a] transition-colors duration-300 rounded-[2px]">{cta.cta_text || "Schedule a conversation"}</Link>
        </div>
      </section>
    </div>
  );
}
